import { defineStore } from "pinia";
import { sdk } from "@/js/sdk";
import {
	AWSS3Bucket,
	DataDrive,
	DataSourceType,
	DataSourceTypeE,
	isLocalMountConfig,
	LocalMountConfig,
} from "@/types/api/dataSources";
import { PreferenceWriteOptions, useUserStore } from "./user";
import * as util from "@/js/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { RemoteAccessState } from "@/types/general";
import { saveOrRevert } from "@/js/optimisticWrites";
import { hydrateCachedList, mirrorStoreListToCache, readCachedList } from "@/js/listCache";
const fallbackDefaultMountConfig = null;

type StoreState = {
	driveList: string[] | undefined;
	driveConfigs: Record<string, DataDrive>;
	bucketList: string[] | undefined;
	bucketRecords: Record<string, AWSS3Bucket>;
	defaultMountConfig: undefined | null | LocalMountConfig;
	fallbackDefaultMountConfig: undefined | null | LocalMountConfig;
	driveFetchStatus: RemoteAccessState;
	bucketFetchStatus: RemoteAccessState;
};

type DataSourceKind = "drive" | "bucket";

type CachedRecord = Record<string, unknown>;

type DataSourceKindConfig = {
	listState: "driveList" | "bucketList";
	recordsState: "driveConfigs" | "bucketRecords";
	statusState: "driveFetchStatus" | "bucketFetchStatus";
	idField: string;
	isCachedRecord: (entry: unknown) => entry is CachedRecord;
	requestIDs: () => Promise<string[] | undefined>;
	transaction: string;
};

function isCachedID(entry: unknown): entry is string {
	return typeof entry === "string";
}

function hasStringField(entry: unknown, field: string): entry is CachedRecord {
	return typeof entry === "object" && entry !== null && typeof (entry as CachedRecord)[field] === "string";
}

const DATA_SOURCE_KINDS: Record<DataSourceKind, DataSourceKindConfig> = {
	drive: {
		listState: "driveList",
		recordsState: "driveConfigs",
		statusState: "driveFetchStatus",
		idField: "DriveID",
		isCachedRecord: (entry): entry is CachedRecord =>
			hasStringField(entry, "DriveID") && Array.isArray(entry.DataSources),
		requestIDs: async () => (await sdk.drives.list()).drive_ids,
		transaction: "Fetch user drives list",
	},
	bucket: {
		listState: "bucketList",
		recordsState: "bucketRecords",
		statusState: "bucketFetchStatus",
		idField: "BucketID",
		isCachedRecord: (entry): entry is CachedRecord => hasStringField(entry, "BucketID"),
		requestIDs: async () => (await sdk.buckets.list()).bucket_ids,
		transaction: "Fetch user buckets list",
	},
};

const KIND_OF_DATA_SOURCE_TYPE: Partial<Record<DataSourceType, DataSourceKind>> = {
	Drive: "drive",
	Bucket: "bucket",
};

function listCacheKey(kind: DataSourceKind): string {
	return `data-${kind}-list`;
}

function recordsCacheKey(kind: DataSourceKind): string {
	return `data-${kind}-records`;
}

function recordsInList<T>(list: string[] | undefined, records: Record<string, T>): T[] | undefined {
	return list?.flatMap((id) => (records[id] ? [records[id]] : []));
}

export const useDataSourcesStore = defineStore("dataSources", {
	state: (): StoreState => ({
		driveList: undefined,
		driveConfigs: {},
		bucketList: undefined,
		bucketRecords: {},
		defaultMountConfig: undefined,
		fallbackDefaultMountConfig: undefined,
		driveFetchStatus: { loading: false, error: false, message: "" },
		bucketFetchStatus: { loading: false, error: false, message: "" },
	}),
	getters: {
		getDefaultMountConfig: (state) => {
			//! Deprecated
			if (isLocalMountConfig(state.defaultMountConfig)) {
				return state.defaultMountConfig;
			} else {
				return fallbackDefaultMountConfig;
			}
		},
		driveIDs: (state): string[] => state.driveList ?? [],
		bucketIDs: (state): string[] => state.bucketList ?? [],
		loadError(state): boolean {
			return state.driveFetchStatus.error || state.bucketFetchStatus.error;
		},
		savedRecordOf(state) {
			return (dataSourceType: DataSourceType, dataSourceID: string): DataDrive | AWSS3Bucket | undefined => {
				const kind = KIND_OF_DATA_SOURCE_TYPE[dataSourceType];
				return kind && state[DATA_SOURCE_KINDS[kind].recordsState][dataSourceID];
			};
		},
	},
	actions: {
		setDriveList(driveIDList: string[]) {
			this.driveList = driveIDList;
		},
		setBucketList(bucketIDList: string[]) {
			this.bucketList = bucketIDList;
		},
		async fetchDriveList() {
			await this.fetchDataSourceList("drive");
		},
		async fetchBucketList() {
			await this.fetchDataSourceList("bucket");
		},
		async fetchDataSourceList(kind: DataSourceKind): Promise<void> {
			const { listState, recordsState, statusState, requestIDs, transaction } = DATA_SOURCE_KINDS[kind];
			let listAnswered = false;
			this[statusState].loading = true;
			await Promise.all([
				this.hydrateDataSourceListFromCache(kind, () => listAnswered),
				(async () => {
					try {
						const ids = await requestIDs();
						listAnswered = true;
						this.mirrorDataSourceListsToCache(kind);
						this[listState] = ids ?? [];
						this[statusState].error = false;
					} catch (error) {
						this[statusState].error = true;
						monitoringCaptureError(error, transaction);
					} finally {
						this[statusState].loading = false;
					}
				})(),
			]);
			if (listAnswered) {
				const freshIDs = new Set(this[listState]);
				const recordsInMemory: CachedRecord = this[recordsState];
				for (const id of Object.keys(recordsInMemory)) {
					if (!freshIDs.has(id)) {
						delete recordsInMemory[id];
					}
				}
			}
		},
		async hydrateDataSourceListFromCache(kind: DataSourceKind, listAnswered: () => boolean): Promise<void> {
			const { listState } = DATA_SOURCE_KINDS[kind];
			const [painted] = await Promise.all([
				hydrateCachedList(
					listCacheKey(kind),
					isCachedID,
					() => this[listState],
					(id) => id,
				),
				this.drawSavedDataSourceRecords(kind),
			]);
			if (painted && !listAnswered()) {
				this[listState] = painted;
				this.mirrorDataSourceListsToCache(kind);
			}
		},
		async drawSavedDataSourceRecords(kind: DataSourceKind): Promise<void> {
			const { recordsState, idField, isCachedRecord } = DATA_SOURCE_KINDS[kind];
			if (Object.keys(this[recordsState]).length > 0) {
				return;
			}
			const savedRecords = await readCachedList(recordsCacheKey(kind), isCachedRecord);
			const recordsInMemory: CachedRecord = this[recordsState];
			for (const record of savedRecords ?? []) {
				recordsInMemory[record[idField] as string] ??= record;
			}
		},
		mirrorDataSourceListsToCache(kind: DataSourceKind): void {
			const { listState, recordsState } = DATA_SOURCE_KINDS[kind];
			mirrorStoreListToCache(this, listCacheKey(kind), () => this[listState]);
			mirrorStoreListToCache(this, recordsCacheKey(kind), () =>
				recordsInList<unknown>(this[listState], this[recordsState]),
			);
		},
		async fetchAllDataSources() {
			await Promise.all([this.fetchDriveList(), this.fetchBucketList()]);
		},
		setBucketRecord(bucket: AWSS3Bucket): void {
			this.bucketRecords[bucket.BucketID] = bucket;
		},
		setDriveConfig(driveConfig: DataDrive) {
			this.driveConfigs[driveConfig.DriveID] = driveConfig;
		},
		async findDrivesMountingResource(resourceID: string): Promise<{ drives: DataDrive[]; allChecked: boolean }> {
			let driveIDs: string[];
			try {
				driveIDs = (await sdk.drives.list()).drive_ids ?? [];
				this.setDriveList(driveIDs);
			} catch (error) {
				monitoringCaptureError(error, "Fetch user drives list");
				return { drives: [], allChecked: false };
			}
			const results = await Promise.allSettled(
				driveIDs.map((id) => sdk.drives.getOne(id).then((r) => r as unknown as DataDrive)),
			);
			results.forEach((result) => {
				if (result.status === "rejected") {
					const reason = result.reason;
					monitoringCaptureError(
						reason instanceof Error ? reason : new Error(String(reason)),
						"Fetch data drive config",
					);
				}
			});
			const fetchedDrives = results.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
			fetchedDrives.forEach((drive) => this.setDriveConfig(drive));
			const drives = fetchedDrives.filter(
				(drive) =>
					Array.isArray(drive.DataSources) &&
					drive.DataSources.some((source) => source.ResourceID === resourceID),
			);
			return { drives, allChecked: fetchedDrives.length === results.length };
		},
		async initialiseDataSourcesFromRemote() {
			this.fetchAllDataSources();
			this.getFallbackMountConfig();
			this.getGlobalDefaultMountConfig();
		},

		async isValidMountConfig(mountConfig: undefined | null | LocalMountConfig): Promise<boolean> {
			if (mountConfig === null) {
				return true;
			}

			if (mountConfig && isLocalMountConfig(mountConfig)) {
				let resourceIDValid: boolean = true;

				if (mountConfig.ResourceType === DataSourceTypeE.SignaloidCloudStorage) {
					const authUserStore = useUserStore();
					const userID = await authUserStore.getCurrentUserID();
					resourceIDValid = mountConfig.ResourceID === `signaloid-cloud-storage:/${userID}` ? true : false;
				}
				return resourceIDValid;
			}
			return false;
		},
		async validateOrGetValidMountConfig(
			mountConfig: undefined | null | LocalMountConfig,
		): Promise<null | LocalMountConfig> {
			if (mountConfig === null) {
				return null;
			}

			if (mountConfig && isLocalMountConfig(mountConfig)) {
				const isValidMountConfig = await this.isValidMountConfig(mountConfig);
				if (isValidMountConfig) {
					return mountConfig;
				}
			}

			// The input mount config is undefined...

			// Try to return global default
			const globalDefaultMountConfig = await this.getGlobalDefaultMountConfig();

			if (globalDefaultMountConfig) {
				return globalDefaultMountConfig;
			}

			// Try to return fallback
			const fallbackMountConfig = await this.getFallbackMountConfig();
			if (fallbackMountConfig) {
				return fallbackMountConfig;
			}

			// if all else fails return null
			return null;
		},

		async setDefaultMountConfig(
			mountConfig: null | LocalMountConfig,
			syncWithRemote: boolean,
			writeOptions: PreferenceWriteOptions = {},
		) {
			const previousMountConfig = this.defaultMountConfig;
			if (isLocalMountConfig(mountConfig)) {
				// valid mount config
				this.defaultMountConfig = mountConfig;
			} else if (mountConfig === null) {
				// user has removed mounting
				this.defaultMountConfig = null;
			} else {
				// unknown mount config, set to default
				this.defaultMountConfig = await this.getFallbackMountConfig();
			}

			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await saveOrRevert(
					() =>
						authUserStore.syncUserPreferenceWithRemote(
							{ Execution_DefaultDataSources: JSON.stringify(mountConfig) },
							writeOptions,
						),
					() => this.defaultMountConfig,
					() => {
						const storedMountConfig = util.parseMountConfigString(
							authUserStore.preferences?.Execution_DefaultDataSources,
						);
						this.defaultMountConfig =
							storedMountConfig !== undefined ? storedMountConfig : previousMountConfig;
					},
				);
			}
		},
		resetDefaultMountConfig() {
			this.setDefaultMountConfig(this.fallbackDefaultMountConfig ?? null, true, { shouldReportFailure: true });
		},
		removeDefaultMountConfig() {
			this.setDefaultMountConfig(null, true, { shouldReportFailure: true });
		},
		async getGlobalDefaultMountConfig() {
			if (this.defaultMountConfig !== undefined) {
				// i.e., the mount config is set or unset (=null)
				return this.defaultMountConfig;
			}
			try {
				const authUserStore = useUserStore();
				const preferencesFromDB = await authUserStore.getCurrentUserPreferences();
				const defaultMountConfigFromDB = preferencesFromDB?.Execution_DefaultDataSources;

				if (defaultMountConfigFromDB && typeof defaultMountConfigFromDB === "string") {
					// try to parse the string
					const parsedMountConfig = util.parseMountConfigString(defaultMountConfigFromDB);
					const isValidMountConfig = await this.isValidMountConfig(parsedMountConfig);
					if (isValidMountConfig) {
						// @ts-ignore
						this.setDefaultMountConfig(parsedMountConfig, false);
						return this.defaultMountConfig;
					} else {
						console.warn("Default mount config in DB is invalid. Resetting to fallback mount config.");
						const validMountConfig = await this.getFallbackMountConfig();
						this.setDefaultMountConfig(validMountConfig, true);
						return this.defaultMountConfig;
					}
				} else {
					// no data source set in DB, set to default mount config
					console.warn("No default mount config set in DB. Resetting to fallback mount config.");
					const validMountConfig = await this.getFallbackMountConfig();
					this.setDefaultMountConfig(validMountConfig, true);
					return this.defaultMountConfig;
				}
			} catch (error) {
				monitoringCaptureError(error, "Get user default mount config");
				return null;
			}
		},

		async getFallbackMountConfig() {
			/*
			 * this function should be called if there is no default set and
			 * you need to get a mount config to fallback onto. It will return
			 * a mount config for the cloud storage if possible, if not it will return `null`
			 */
			if (this.fallbackDefaultMountConfig !== undefined) {
				// i.e., the mount config is set or unset (=null)
				return this.fallbackDefaultMountConfig;
			}
			try {
				const authUserStore = useUserStore();
				const currentUserID = await authUserStore.getCurrentUserID();

				if (!currentUserID) {
					throw new Error("User ID invalid");
				}
				const fallbackMountConfig: LocalMountConfig = {
					Location: "sd0",
					ResourceID: `signaloid-cloud-storage:/${currentUserID}`,
					ResourceType: DataSourceTypeE.SignaloidCloudStorage,
				};

				this.fallbackDefaultMountConfig = fallbackMountConfig;
				return fallbackMountConfig;
			} catch (error) {
				monitoringCaptureError(error, "Get user fallback mount config");
				return null;
			}
		},
	},
});
